import React, { useState, useEffect } from 'react';
import type { BreakpointDef, LayoutItem } from '../Config/config';
import { useSimulatedBreakpoint } from './hooks/useBreakpoint';

interface CellInfo {
  col: number;
  row: number;
}

export const GridInteractive: React.FC<{
  breakpoints: BreakpointDef[];
  items: LayoutItem[];
  gap?: number;
  gridRef?: React.RefObject<HTMLDivElement | null>;
  onDisplayRowsChange?: (rows: number) => void;
  onSelectionChange?: (position: { colStart: number; colEnd: number; rowStart: number; rowEnd: number } | null) => void;
  onComponentClick?: (componentId: string) => void;
  onSelectingStateChange?: (isSelecting: boolean) => void;
  simulatedWidth?: number;
}> = ({ breakpoints, items, gap = 0, gridRef, onDisplayRowsChange, onSelectionChange, onComponentClick, onSelectingStateChange, simulatedWidth }) => {
  const { currentBreakpoint: bp } = useSimulatedBreakpoint(breakpoints, simulatedWidth);
  const cols = bp.columns;
  
  const [hoveredCell, setHoveredCell] = useState<CellInfo | null>(null);
  const [selectedCells, setSelectedCells] = useState<CellInfo[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<CellInfo | null>(null);
  
  const [viewportWidth, setViewportWidth] = useState(simulatedWidth || (typeof window !== 'undefined' ? window.innerWidth : 1920));
  const [gridHeight, setGridHeight] = useState(1080);

  useEffect(() => {
    let timeoutId: number;
    
    const updateSize = () => {
      // Debouncer les mises à jour pour éviter les recalculs trop fréquents
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        setViewportWidth(simulatedWidth || window.innerWidth);
        if (gridRef?.current) {
          // Prendre la hauteur réelle de la grille
          setGridHeight(gridRef.current.scrollHeight);
        }
      }, 50); // Petit délai pour éviter les recalculs trop fréquents
    };
    
    updateSize();
    
    // Observer les changements de taille de la grille
    if (gridRef?.current) {
      const resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(gridRef.current);
      
      window.addEventListener('resize', updateSize, { passive: true });
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateSize);
      };
    }
    
    window.addEventListener('resize', updateSize, { passive: true });
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', updateSize);
    };
  }, [gridRef, simulatedWidth]);

  // Mettre à jour la largeur quand la simulation change
  useEffect(() => {
    setViewportWidth(simulatedWidth || window.innerWidth);
  }, [simulatedWidth]);

  const cellSize = (viewportWidth - gap * (cols - 1)) / cols;
  const baseNumRows = Math.ceil(gridHeight / cellSize);
  
  // Nombre minimal de lignes pour permettre la sélection en dehors
  const [extraRows, setExtraRows] = useState(10); // Toujours au moins 10 lignes supplémentaires
  
  // Augmenter le nombre de lignes si on s'approche du bord
  useEffect(() => {
    if (selectedCells.length > 0) {
      const maxRow = Math.max(...selectedCells.map(c => c.row));
      const currentDisplayRows = baseNumRows + extraRows;
      
      // Si on est à moins de 3 lignes du bord, ajouter 10 lignes
      if (maxRow >= currentDisplayRows - 3) {
        setExtraRows(prev => prev + 10);
      }
    }
  }, [selectedCells, baseNumRows, extraRows]);
  
  const displayRows = baseNumRows + extraRows;
  
  // Limiter le nombre de lignes pour éviter les performances dégradées
  const maxRows = 100; // Maximum 100 lignes pour éviter le lag
  const limitedDisplayRows = Math.min(displayRows, maxRows);
  
  // Notifier le parent du nombre de lignes affichées (mémorisé pour éviter les boucles)
  const currentDisplayRows = selectedCells.length > 0 ? limitedDisplayRows : Math.min(baseNumRows, maxRows);
  
  useEffect(() => {
    if (onDisplayRowsChange) {
      onDisplayRowsChange(currentDisplayRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDisplayRows]);

  // Trouver le composant dans une case
  const findComponentInCell = (col: number, row: number): LayoutItem | null => {
    for (const item of items) {
      if (item.outsideGrid) continue;
      const pos = item.positions?.[bp.name];
      if (!pos) continue;
      
      if (col >= (pos.colStart ?? 0) && col < (pos.colEnd ?? 0) &&
          row >= (pos.rowStart ?? 0) && row < (pos.rowEnd ?? 0)) {
        return item;
      }
    }
    return null;
  };

  const handleCellMouseDown = (col: number, row: number, component?: LayoutItem) => {
    // Si on clique sur un composant, le sélectionner
    if (component && onComponentClick) {
      onComponentClick(component.id);
      return;
    }
    
    setIsSelecting(true);
    setSelectionStart({ col, row });
    setSelectedCells([{ col, row }]);
  };

  const handleCellMouseEnter = (col: number, row: number) => {
    setHoveredCell({ col, row });
    
    if (isSelecting && selectionStart) {
      // Calculer la sélection rectangulaire
      const minCol = Math.min(selectionStart.col, col);
      const maxCol = Math.max(selectionStart.col, col);
      const minRow = Math.min(selectionStart.row, row);
      const maxRow = Math.max(selectionStart.row, row);
      
      const newSelection: CellInfo[] = [];
      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          newSelection.push({ col: c, row: r });
        }
      }
      setSelectedCells(newSelection);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
  };

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);
  
  // Notifier le parent quand l'état de sélection change
  useEffect(() => {
    if (onSelectingStateChange) {
      onSelectingStateChange(isSelecting);
    }
  }, [isSelecting, onSelectingStateChange]);

  // Calculer les paramètres de position
  const getSelectionParams = () => {
    if (selectedCells.length === 0) return null;
    
    const cols = selectedCells.map(c => c.col);
    const rows = selectedCells.map(c => c.row);
    
    return {
      colStart: Math.min(...cols) + 1,
      colEnd: Math.max(...cols) + 2,
      rowStart: Math.min(...rows) + 1,
      rowEnd: Math.max(...rows) + 2
    };
  };

  const selectionParams = getSelectionParams();
  const hoveredComponent = hoveredCell ? findComponentInCell(hoveredCell.col + 1, hoveredCell.row + 1) : null;
  
  // Notifier le parent quand la sélection change
  useEffect(() => {
    if (!onSelectionChange) return;
    
    if (selectionParams) {
      onSelectionChange(selectionParams);
    } else if (selectedCells.length === 0) {
      onSelectionChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCells.length, selectionParams?.colStart, selectionParams?.colEnd, selectionParams?.rowStart, selectionParams?.rowEnd]);

  return (
    <>
      {/* Grille interactive invisible */}
      <div 
        style={{ 
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          minHeight: selectedCells.length > 0 ? `${displayRows * cellSize + gap * (displayRows - 1)}px` : '100%',
          height: selectedCells.length > 0 ? `${displayRows * cellSize + gap * (displayRows - 1)}px` : 'auto',
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          columnGap: `${gap}px`,
          rowGap: `${gap}px`,
          gridAutoRows: `${cellSize}px`,
          zIndex: 10000,
          pointerEvents: 'auto',
          userSelect: 'none'
        }}
      >
        {Array.from({ length: cols * currentDisplayRows }, (_, i) => {
          const col = i % cols;
          const row = Math.floor(i / cols);
          const isSelected = selectedCells.some(c => c.col === col && c.row === row);
          const component = findComponentInCell(col + 1, row + 1);
          
          return (
            <div
              key={`cell-${col}-${row}`}
              onMouseDown={() => handleCellMouseDown(col, row, component ?? undefined)}
              onMouseEnter={(e) => {
                handleCellMouseEnter(col, row);
                // Highlight au survol
                if (!isSelected && component) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              style={{
                backgroundColor: isSelected ? 'rgba(0, 123, 255, 0.3)' : 'transparent',
                border: isSelected ? '2px solid rgba(0, 123, 255, 0.8)' : 'none',
                cursor: component ? 'pointer' : 'crosshair',
                position: 'relative',
                boxSizing: 'border-box',
                transition: 'background-color 0.1s',
                outline: component ? '2px solid rgba(255, 255, 0, 0.3)' : 'none',
                outlineOffset: '-2px'
              }}
            />
          );
        })}
      </div>

      {/* Tooltip au survol */}
      {hoveredCell && (
        <div
          style={{
            position: 'fixed',
            left: `${(hoveredCell.col * cellSize) + 10}px`,
            top: `${(hoveredCell.row * cellSize) + 10}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 10001,
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          {hoveredComponent ? (
            <div>
              <strong>{hoveredComponent.component}</strong>
              <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.8 }}>
                ID: {hoveredComponent.id}
              </div>
            </div>
          ) : (
            <div>
              Colonne: {hoveredCell.col + 1}, Ligne: {hoveredCell.row + 1}
            </div>
          )}
        </div>
      )}

      {/* Panneau de sélection */}
      {selectionParams && selectedCells.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '8px',
            fontSize: '14px',
            zIndex: 10002,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            fontFamily: 'monospace'
          }}
        >
          <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
            Paramètres de position :
          </div>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '8px', borderRadius: '4px' }}>
            colStart: {selectionParams.colStart}, colEnd: {selectionParams.colEnd},<br />
            rowStart: {selectionParams.rowStart}, rowEnd: {selectionParams.rowEnd}
          </div>
          <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.7 }}>
            {selectedCells.length} case{selectedCells.length > 1 ? 's' : ''} sélectionnée{selectedCells.length > 1 ? 's' : ''}
          </div>
          <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                const text = `colStart: ${selectionParams.colStart}, colEnd: ${selectionParams.colEnd}, rowStart: ${selectionParams.rowStart}, rowEnd: ${selectionParams.rowEnd}`;
                navigator.clipboard.writeText(text);
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Copier
            </button>
            <button
              onClick={() => {
                setSelectedCells([]);
                setSelectionStart(null);
                setExtraRows(10); // Réinitialiser à 10 lignes supplémentaires
                if (onSelectionChange) {
                  onSelectionChange(null); // Notifier qu'il n'y a plus de sélection
                }
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GridInteractive;
